import React, { useEffect, useMemo, useState } from 'react';
import { fetchAlertHistory } from '../../services/logApi';
import './LogManagement.css';

const PAGE_SIZE = 10;
const PAGE_GROUP_SIZE = 5;

export default function LogAlim() {
  const [logAlimList, setLogAlimList] = useState([]);
  const [inputQuery, setInputQuery] = useState('');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getLogsAlim = async () => {
      try {
        const token = localStorage.getItem('token');
	const logs = await fetchAlertHistory(token);
        setLogAlimList(Array.isArray(logs) ? logs : []);
      } catch (error) {
        console.error('알림 로그 요청 실패:', error);
        alert('알림 로그 목록을 가져오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    getLogsAlim();
  }, []);

  const filteredLogs = useMemo(() => {
    const keyword = query.toLowerCase();

    if (!keyword) return logAlimList;

    return logAlimList.filter((log) =>
      [
	  log.user_name,
	  log.admin_name,
	  log.admin_username,
	  log.action_type,
	  log.alert_id,
	  log.create_date
      ].some((value) =>
        String(value ?? '').toLowerCase().includes(keyword)
      )
    );
  }, [logAlimList, query]);

  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);
  const currentLogs = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const currentGroup = Math.floor((currentPage - 1) / PAGE_GROUP_SIZE);
  const startPage = currentGroup * PAGE_GROUP_SIZE + 1;
  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, totalPages);

  const handleSearch = () => {
    setQuery(inputQuery.trim());
    setCurrentPage(1);
  };

  const handleReset = () => {
    setInputQuery('');
    setQuery('');
    setCurrentPage(1);
  };

  return (
    <section className="lm-section">
      <div className="lm-toolbar">
        <div className="lm-search">
          <input
            type="text"
            placeholder="알림 내용, 발생 일시 검색"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                handleSearch();
              }
            }}
          />
          <button type="button" onClick={handleSearch}>검색</button>
          <button type="button" className="lm-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="lm-table-top">
        <strong>조회 결과 {filteredLogs.length.toLocaleString()}건</strong>
        <span>응급 알림 발생 및 처리 내역</span>
      </div>

      <div className="lm-table-wrap">
	  <table className="lm-table">
	    <colgroup>
	      <col style={{ width: '8%' }} />
	      <col style={{ width: '18%' }} />
	      <col style={{ width: '30%' }} />
	      <col style={{ width: '22%' }} />
	      <col style={{ width: '22%' }} />
	    </colgroup>
	    <thead>
	      <tr>
	        <th>No</th>
	        <th>대상자</th>
	        <th>알림 내용</th>
	        <th>처리 주체</th>
	        <th>일시</th>
	      </tr>
	    </thead>
	    <tbody>
	      {currentLogs.length > 0 ? (
	        currentLogs.map((log, index) => {
	          const actionLabels = {
	            FIRST_ALERT: '1차 응급 알림 발생',
	            SECOND_ALERT: '2차 응급 알림 발생',
	            RECEIVED: '응급상황 접수',
	            VIEWED: '응급상황 확인',
	            CLOSED: '응급상황 종료',
	          };

	          return (
	            <tr key={log.history_id}>
	              <td>
	                {filteredLogs.length - (currentPage - 1) * PAGE_SIZE - index}
	              </td>
	              <td>{log.user_name || '-'}</td>
	              <td className="lm-content-cell">
	                {actionLabels[log.action_type] || log.action_type || '-'}
	              </td>
	              <td>{log.admin_name || '-'}</td>
	              <td className="lm-date">
	                {log.create_date
	                  ? new Date(log.create_date).toLocaleString('ko-KR', {
	                      timeZone: 'Asia/Seoul',
	                      year: 'numeric',
	                      month: '2-digit',
	                      day: '2-digit',
	                      hour: '2-digit',
	                      minute: '2-digit',
	                      second: '2-digit',
	                      hour12: false,
	                    })
	                  : '-'}
	              </td>
	            </tr>
	          );
	        })
	      ) : (
	        <tr>
	          <td colSpan={5} className="lm-empty">
	            {loading
	              ? '알림 이력을 불러오는 중입니다.'
	              : '조회된 알림 이력이 없습니다.'}
	          </td>
	        </tr>
	      )}
	    </tbody>
	  </table>
	</div>

      <div className="lm-footer">
        <span>
          총 {filteredLogs.length.toLocaleString()}건
          {totalPages > 0 && ` · ${currentPage} / ${totalPages} 페이지`}
        </span>

        {totalPages > 1 && (
          <div className="lm-pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>‹</button>
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            ).map((page) => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>›</button>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        )}
      </div>
    </section>
  );
}
